import React, { useCallback, useEffect, useState, Component } from 'react';
import { ArrowRight, ArrowLeft, Check, X, Star, ShieldCheck, Lock, Download, Gift } from 'lucide-react';
// Types
type QuizSection = 'intro' | 'questions' | 'loading' | 'results' | 'offer' | 'checkout' | 'thankyou';
type BellyType = 'TYPE1' | 'TYPE2' | 'TYPE3' | 'TYPE4';
type PrimaryGoal = 'BELLY' | 'ENERGY' | 'SKIN' | 'IDENTITY';
interface QuizState {
  currentSection: QuizSection;
  currentQuestion: number;
  answers: Record<number, string | string[]>;
  points: {
    TYPE1: number;
    TYPE2: number;
    TYPE3: number;
    TYPE4: number;
  };
  tags: Set<string>;
  primaryGoal: PrimaryGoal | null;
  resultType: BellyType | null;
}
const QUESTIONS = [{
  id: 1,
  progress: 7,
  headline: "First, let's make sure this quiz is right for you.",
  question: 'How old are you?',
  type: 'single' as const,
  options: [{
    value: 'under45',
    label: 'Under 45',
    tag: 'UNDER_45'
  }, {
    value: '45-49',
    label: '45 – 49',
    tag: 'AGE_45_49'
  }, {
    value: '50-54',
    label: '50 – 54',
    tag: 'AGE_50_54'
  }, {
    value: '55-59',
    label: '55 – 59',
    tag: 'AGE_55_59'
  }, {
    value: '60plus',
    label: '60 or older',
    tag: 'AGE_60_PLUS'
  }]
}, {
  id: 2,
  progress: 14,
  question: 'Where are you in your menopause journey right now?',
  subtext: 'This helps us identify which hormonal shifts are most likely affecting your body at this moment.',
  type: 'single' as const,
  options: [{
    value: 'peri',
    label: '🌊 Perimenopause',
    description: '(irregular periods, mood swings beginning)',
    tag: 'PERI'
  }, {
    value: 'meno',
    label: '🔥 Menopause',
    description: '(periods stopped in the last 1-2 years)',
    tag: 'MENO'
  }, {
    value: 'post',
    label: '🌿 Post-Menopause',
    description: '(periods stopped 2+ years ago)',
    tag: 'POST_MENO'
  }, {
    value: 'unsure',
    label: "🤷 I'm not sure",
    description: '(I just know something has changed)',
    tag: 'NOT_SURE'
  }]
}, {
  id: 3,
  progress: 21,
  question: 'When you look in the mirror, which of these bothers you the MOST right now?',
  subtext: 'Be honest — this is just between you and your results.',
  type: 'single' as const,
  options: [{
    value: 'belly',
    label: '👗 My belly and waistline',
    description: '(feels "puffy" or "inflated" most days)',
    tag: 'GOAL_BELLY'
  }, {
    value: 'face',
    label: '😶 My face and jawline',
    description: '(puffiness, loss of definition)',
    tag: 'GOAL_FACE'
  }, {
    value: 'skin',
    label: '✋ My skin overall',
    description: '(losing firmness, elasticity, "snap")',
    tag: 'GOAL_SKIN'
  }, {
    value: 'body',
    label: '💪 My whole body',
    description: '(feels puffy and heavier everywhere)',
    tag: 'GOAL_FULL_BODY'
  }]
}, {
  id: 4,
  progress: 28,
  headline: 'Think about your belly bloating specifically.',
  question: 'When does it feel the WORST?',
  subtext: "There's a hormonal reason for the timing — and it tells us a lot about what's happening inside your body.",
  type: 'single' as const,
  options: [{
    value: 'morning',
    label: '🌅 In the morning',
    description: '(I wake up already feeling puffy)',
    tag: 'BLOAT_MORNING'
  }, {
    value: 'midday',
    label: '☀️ Mid-morning to early afternoon',
    description: '(starts building after breakfast)',
    tag: 'BLOAT_MIDDAY'
  }, {
    value: '4pm',
    label: '🕓 Late afternoon (3–6 PM)',
    description: '(I look "6 months pregnant" by dinner)',
    tag: 'BLOAT_4PM',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'night',
    label: '🌙 At night',
    description: '(worst after dinner, before bed)',
    tag: 'BLOAT_NIGHT'
  }, {
    value: 'allday',
    label: '⏰ All day, every day',
    description: '(never really goes away anymore)',
    tag: 'BLOAT_CHRONIC',
    points: {
      TYPE2: 2
    }
  }]
}, {
  id: 5,
  progress: 35,
  question: 'Which of these have you already tried to fix the bloating and belly fat?',
  subtext: "This helps us eliminate what ISN'T working for your body type — so we don't recommend more of the same.",
  type: 'multi' as const,
  options: [{
    value: 'cardio',
    label: '🏃 Cardio or intense exercise',
    description: '(walking, running, gym classes)',
    tag: 'TRIED_CARDIO'
  }, {
    value: 'diet',
    label: '🥗 Calorie restriction or diet',
    description: '(eating less, cutting carbs, salads)',
    tag: 'TRIED_DIET'
  }, {
    value: 'supps',
    label: '💊 Supplements or vitamins',
    description: '(probiotics, collagen, weight loss pills)',
    tag: 'TRIED_SUPPS'
  }, {
    value: 'water',
    label: '💧 Drinking more water',
    description: '(trying to "flush it out")',
    tag: 'TRIED_WATER'
  }, {
    value: 'elimination',
    label: '🚫 Cutting out certain foods',
    description: '(gluten, dairy, sugar, processed food)',
    tag: 'TRIED_ELIMINATION'
  }, {
    value: 'nothing',
    label: '😔 Nothing yet',
    description: "(I don't know where to start)",
    tag: 'BEGINNER'
  }]
}, {
  id: 6,
  progress: 42,
  question: 'How would you describe your energy levels on a typical day?',
  subtext: 'Fatigue and bloating often share the same hormonal root — your answer helps us connect the pattern.',
  type: 'single' as const,
  options: [{
    value: 'exhausted',
    label: '😴 Exhausted most of the day',
    description: "(tired even after a full night's sleep)",
    tag: 'FATIGUE_SEVERE'
  }, {
    value: 'crashes',
    label: '📉 Crashes in the afternoon',
    description: '(I hit a wall around 2–4 PM every day)',
    tag: 'FATIGUE_PM',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'inconsistent',
    label: '🎢 Very inconsistent',
    description: '(good days and bad days, no pattern)',
    tag: 'FATIGUE_VARIABLE'
  }, {
    value: 'okay',
    label: '😐 Okay, but not what it used to be',
    description: '(I function, but miss my old energy)',
    tag: 'FATIGUE_MILD'
  }]
}, {
  id: 7,
  progress: 49,
  headline: "Here's an important one.",
  question: 'Does your belly seem to bloat MORE on stressful days?',
  subtext: 'This is one of the most revealing questions in the entire quiz. Answer honestly.',
  type: 'single' as const,
  options: [{
    value: 'yes-absolutely',
    label: '✅ Yes — absolutely',
    description: '(stress days are always my worst bloat days)',
    tag: 'CORTISOL_CONFIRMED',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'yes-thinking',
    label: '🤔 Now that I think about it… yes',
    description: '(I never made that connection before)',
    tag: 'CORTISOL_LIKELY',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'not-sure',
    label: "❓ I'm not sure",
    description: "(I haven't tracked it)",
    tag: 'TYPE3_TAG',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'not-really',
    label: '❌ Not really',
    description: "(doesn't seem connected to stress for me)",
    tag: 'TYPE2_TAG',
    points: {
      TYPE2: 1
    }
  }]
}, {
  id: 8,
  progress: 56,
  headline: 'Be honest with yourself on this one.',
  question: "Have you noticed that eating LESS hasn't actually helped your belly?",
  subtext: "This surprises most women — but there's a biological reason it happens after 45.",
  type: 'single' as const,
  options: [{
    value: 'worse',
    label: '😩 Yes — eating less actually made it WORSE',
    description: '(my belly got bigger when I cut calories)',
    tag: 'STARVATION_MODE',
    points: {
      TYPE4: 2
    }
  }, {
    value: 'no-change',
    label: '😐 Yes — no change at all',
    description: '(I ate less and nothing happened)',
    tag: 'METABOLISM_SLOWED',
    points: {
      TYPE4: 1
    }
  }, {
    value: 'havent-tried',
    label: "🤷 I haven't really tried restricting calories",
    description: '(not sure if it would help me)',
    tag: 'EDUCATION_NEEDED'
  }, {
    value: 'used-to-work',
    label: '✅ It used to work, but stopped working',
    description: '(it worked in my 30s — not anymore)',
    tag: 'AGE_SHIFT',
    points: {
      TYPE4: 1
    }
  }]
}, {
  id: 9,
  progress: 60,
  question: 'How often do you do intense exercise — like running, HIIT, spin class, or heavy cardio?',
  subtext: 'This might surprise you — but exercise intensity plays a major role in belly bloating after 45.',
  type: 'single' as const,
  options: [{
    value: 'every-day',
    label: '🏋️ Every day or almost every day',
    description: '(I work out hard — and still bloat)',
    tag: 'HIGH_EXERCISE',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'few-times',
    label: '🚴 A few times a week',
    description: '(regular workouts, moderate intensity)',
    tag: 'MOD_EXERCISE',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'light',
    label: '🚶 Mostly walking or light movement',
    description: '(I keep it gentle these days)',
    tag: 'LOW_EXERCISE',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'little',
    label: '😔 Little to no exercise',
    description: '(too tired, too busy, or it hurts)',
    tag: 'SEDENTARY',
    points: {
      TYPE4: 1
    }
  }]
}, {
  id: 10,
  progress: 67,
  question: 'Which of these "mystery symptoms" do you experience regularly?',
  subtext: 'These symptoms are rarely talked about together — but they share a common hormonal root.',
  type: 'multi' as const,
  options: [{
    value: 'puffy-face',
    label: '😶 Puffy face in the morning',
    description: '(especially around eyes and cheeks)',
    tag: 'PUFFY_FACE',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'swollen-ankles',
    label: '👣 Swollen ankles or feet by evening',
    description: '(shoes feel tight by end of day)',
    tag: 'SWOLLEN_ANKLES',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'brain-fog',
    label: '🧠 Brain fog or poor concentration',
    description: '(hard to think clearly, forgetting things)',
    tag: 'BRAIN_FOG',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'poor-sleep',
    label: '😴 Poor or restless sleep',
    description: "(can't fall asleep or wake up at 3 AM)",
    tag: 'POOR_SLEEP',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'inflamed',
    label: '🌡️ Feeling "inflamed" or achy',
    description: '(joints hurt, body feels heavy)',
    tag: 'INFLAMED',
    points: {
      TYPE2: 2
    }
  }, {
    value: 'all',
    label: '✅ ALL of the above',
    description: '(this is basically my daily life)',
    tag: 'ALL_SYMPTOMS',
    points: {
      TYPE2: 2,
      TYPE3: 2
    }
  }]
}, {
  id: 11,
  progress: 74,
  headline: 'Has this ever happened to you?',
  question: 'You wake up feeling flat and almost "normal"... ...but by evening, you look 3 months pregnant.',
  subtext: "If this sounds familiar, it's one of the most important clues about what's happening in your body.",
  type: 'single' as const,
  options: [{
    value: 'yes-every-day',
    label: '😩 YES — this happens to me almost every day',
    tag: 'DIURNAL_BLOAT',
    points: {
      TYPE1: 2
    }
  }, {
    value: 'sometimes',
    label: '😐 Sometimes — maybe a few times a week',
    tag: 'DIURNAL_BLOAT_LIKELY',
    points: {
      TYPE1: 1
    }
  }, {
    value: 'occasionally',
    label: '🤔 Occasionally — usually on certain days',
    tag: 'OCCASIONAL',
    points: {
      TYPE3: 1
    }
  }, {
    value: 'not-really',
    label: "❌ Not really — my bloating doesn't vary much",
    tag: 'STABLE_BLOAT',
    points: {
      TYPE2: 1
    }
  }]
}, {
  id: 12,
  progress: 81,
  headline: 'Almost done. This question is important.',
  question: 'If you had a simple, natural 5-minute morning ritual designed specifically for YOUR hormonal type... How committed would you be to actually trying it?',
  subtext: 'There are no wrong answers — this helps us personalize your plan.',
  type: 'single' as const,
  options: [{
    value: 'committed',
    label: '💪 100% committed',
    description: "(I'm done guessing — I'll follow it exactly)",
    tag: 'HIGH_INTENT'
  }, {
    value: 'pretty-committed',
    label: '👍 Pretty committed',
    description: "(I'll give it a real try for at least 7 days)",
    tag: 'WARM_BUYER'
  }, {
    value: 'cautious',
    label: '🤞 Cautiously optimistic',
    description: "(I'm skeptical, but open to it)",
    tag: 'SKEPTIC_BUYER'
  }, {
    value: 'desperate',
    label: "🙏 Desperate — I'll try anything at this point",
    description: '(nothing has worked and I need answers)',
    tag: 'URGENT_BUYER'
  }]
}, {
  id: 13,
  progress: 88,
  headline: 'Last personal question.',
  question: 'What matters most to you right now?',
  subtext: 'Your answer determines which part of your personalized plan we emphasize most.',
  type: 'single' as const,
  options: [{
    value: 'belly',
    label: '👗 A flatter, less bloated belly',
    description: '(I want to button my jeans again)',
    primaryGoal: 'BELLY' as PrimaryGoal
  }, {
    value: 'energy',
    label: '⚡ More energy throughout the day',
    description: '(I miss feeling like myself)',
    primaryGoal: 'ENERGY' as PrimaryGoal
  }, {
    value: 'skin',
    label: '✨ Firmer, more youthful skin',
    description: "(I want my 'glow' back)",
    primaryGoal: 'SKIN' as PrimaryGoal
  }, {
    value: 'identity',
    label: '🧘 Feeling like ME again',
    description: '(confident, comfortable, in control)',
    primaryGoal: 'IDENTITY' as PrimaryGoal
  }]
}, {
  id: 14,
  progress: 95,
  headline: 'One final question — and then your personalized results are ready.',
  question: "What do you think has been the BIGGEST reason this problem hasn't been solved yet?",
  subtext: 'Understanding this helps us make sure your plan actually addresses the ROOT cause.',
  type: 'single' as const,
  options: [{
    value: 'didnt-know',
    label: "🔍 I didn't know what was actually causing it",
    description: '(nobody explained the real reason to me)',
    tag: 'EDUCATION_BUYER'
  }, {
    value: 'tried-everything',
    label: "😩 I've tried everything and nothing worked",
    description: "(I'm starting to think nothing will)",
    tag: 'EXHAUSTED_BUYER'
  }, {
    value: 'no-time',
    label: "⏰ I don't have time for complicated routines",
    description: '(I need something simple and fast)',
    tag: 'BUSY_BUYER'
  }, {
    value: 'wasted-money',
    label: "💸 I've wasted money on things that don't work",
    description: "(I'm skeptical of spending more)",
    tag: 'SKEPTIC_BUYER'
  }]
}];
export function QuizFunnel() {
  const [state, setState] = useState<QuizState>({
    currentSection: 'intro',
    currentQuestion: 1,
    answers: {},
    points: {
      TYPE1: 0,
      TYPE2: 0,
      TYPE3: 0,
      TYPE4: 0
    },
    tags: new Set(),
    primaryGoal: null,
    resultType: null
  });
  const handleAnswer = useCallback((questionId: number, answer: string | string[]) => {
    setState(prev => {
      const newAnswers = {
        ...prev.answers,
        [questionId]: answer
      };
      const question = QUESTIONS.find(q => q.id === questionId);
      let newPoints = {
        ...prev.points
      };
      const newTags = new Set(prev.tags);
      let newPrimaryGoal = prev.primaryGoal;
      if (question) {
        if (question.type === 'single') {
          const option = question.options.find(o => o.value === answer);
          if (option) {
            if ('tag' in option && option.tag) newTags.add(option.tag);
            if ('points' in option && option.points) {
              Object.entries(option.points).forEach(([type, points]) => {
                newPoints[type as keyof typeof newPoints] += points;
              });
            }
            if ('primaryGoal' in option && option.primaryGoal) {
              newPrimaryGoal = option.primaryGoal;
            }
          }
        } else if (question.type === 'multi' && Array.isArray(answer)) {
          answer.forEach(val => {
            const option = question.options.find(o => o.value === val);
            if (option) {
              if ('tag' in option && option.tag) newTags.add(option.tag);
              if ('points' in option && option.points) {
                Object.entries(option.points).forEach(([type, points]) => {
                  newPoints[type as keyof typeof newPoints] += points;
                });
              }
            }
          });
        }
      }
      // Auto-advance for single-select
      const isLastQuestion = questionId === 14;
      if (question?.type === 'single') {
        if (isLastQuestion) {
          // Calculate result type
          const resultType = Object.entries(newPoints).reduce((a, b) => b[1] > a[1] ? b : a)[0] as BellyType;
          return {
            ...prev,
            answers: newAnswers,
            points: newPoints,
            tags: newTags,
            primaryGoal: newPrimaryGoal,
            resultType,
            currentSection: 'loading'
          };
        } else {
          return {
            ...prev,
            answers: newAnswers,
            points: newPoints,
            tags: newTags,
            primaryGoal: newPrimaryGoal,
            currentQuestion: questionId + 1
          };
        }
      }
      return {
        ...prev,
        answers: newAnswers,
        points: newPoints,
        tags: newTags,
        primaryGoal: newPrimaryGoal
      };
    });
  }, []);
  const handleMultiContinue = useCallback(() => {
    setState(prev => {
      if (prev.currentQuestion === 14) {
        const resultType = Object.entries(prev.points).reduce((a, b) => b[1] > a[1] ? b : a)[0] as BellyType;
        return {
          ...prev,
          resultType,
          currentSection: 'loading'
        };
      }
      return {
        ...prev,
        currentQuestion: prev.currentQuestion + 1
      };
    });
  }, []);
  const handleBack = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentQuestion: Math.max(1, prev.currentQuestion - 1)
    }));
  }, []);
  const startQuiz = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentSection: 'questions'
    }));
  }, []);
  const goToSection = useCallback((section: QuizSection) => {
    setState(prev => ({
      ...prev,
      currentSection: section
    }));
  }, []);
  // Loading screen timer
  useEffect(() => {
    if (state.currentSection === 'loading') {
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          currentSection: 'results'
        }));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [state.currentSection]);
  return <div className="min-h-screen bg-[#FAF7F2]" data-id="element-463">
      {state.currentSection === 'intro' && <IntroSection onStart={startQuiz} data-id="element-464" />}
      {state.currentSection === 'questions' && <QuestionsSection currentQuestion={state.currentQuestion} answers={state.answers} onAnswer={handleAnswer} onMultiContinue={handleMultiContinue} onBack={handleBack} data-id="element-465" />}
      {state.currentSection === 'loading' && <LoadingSection data-id="element-466" />}
      {state.currentSection === 'results' && <ResultsSection resultType={state.resultType!} primaryGoal={state.primaryGoal!} onContinue={() => goToSection('offer')} data-id="element-467" />}
      {state.currentSection === 'offer' && <OfferSection onContinue={() => goToSection('checkout')} data-id="element-468" />}
      {state.currentSection === 'checkout' && <CheckoutSection onSubmit={() => goToSection('thankyou')} data-id="element-469" />}
      {state.currentSection === 'thankyou' && <ThankYouSection data-id="element-470" />}
    </div>;
}
// Section Components
function IntroSection({
  onStart
}: {
  onStart: () => void;
}) {
  return <div className="min-h-screen flex items-center justify-center px-4 py-12" data-id="element-471">
      <div className="w-full max-w-[680px]" data-id="element-472">
        {/* Top Bar */}
        <div className="text-center mb-8" data-id="element-473">
          <p className="text-sm font-semibold text-[#C9973A] mb-2" data-id="element-474">
            The Golden Root Ritual
          </p>
          <p className="text-lg font-bold text-[#2C2C2C]" data-id="element-475">
            Free Menopause Belly Quiz
          </p>
          <div className="w-full h-2 bg-stone-200 rounded-full mt-4" data-id="element-476">
            <div className="h-full bg-[#C9973A] rounded-full" style={{
            width: '0%'
          }} data-id="element-477" />
          </div>
        </div>

        {/* Hero */}
        <div className="text-center mb-12" data-id="element-478">
          <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] leading-tight mb-6" data-id="element-479">
            Why Does Your Belly Bloat Every Afternoon — Even When You're Eating
            Healthy?
          </h1>
          <h2 className="text-xl md:text-2xl text-stone-600 font-medium mb-8" data-id="element-480">
            This free quiz analyzes your symptoms, habits, and hormonal patterns
            to reveal the hidden reason your belly won't budge — and exactly
            what YOUR body needs to flush it out.
          </h2>
          <div className="w-20 h-1 bg-[#C9973A] mx-auto mb-10" data-id="element-481" />
        </div>

        {/* Bullets */}
        <div className="mb-10" data-id="element-482">
          <p className="text-lg font-bold text-[#2C2C2C] mb-6 text-center" data-id="element-483">
            In the next 60 seconds, you'll find out:
          </p>
          <ul className="space-y-4 text-left" data-id="element-484">
            <li className="flex items-start gap-3" data-id="element-485">
              <span className="text-2xl flex-shrink-0" data-id="element-486">🔍</span>
              <span className="text-stone-700" data-id="element-487">
                Which of the 4 Menopause Belly Types you have — and why this
                changes everything about how you should approach your body right
                now
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-488">
              <span className="text-2xl flex-shrink-0" data-id="element-489">⚡</span>
              <span className="text-stone-700" data-id="element-490">
                The hidden hormonal trigger that causes your belly to inflate
                every afternoon — even if you've been "doing everything right"
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-491">
              <span className="text-2xl flex-shrink-0" data-id="element-492">🚫</span>
              <span className="text-stone-700" data-id="element-493">
                Why your current approach is likely making it worse — and the
                one thing women over 45 should STOP doing immediately
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-494">
              <span className="text-2xl flex-shrink-0" data-id="element-495">🌿</span>
              <span className="text-stone-700" data-id="element-496">
                The natural 5-minute morning habit that works WITH your post-45
                biology instead of fighting it
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-497">
              <span className="text-2xl flex-shrink-0" data-id="element-498">📋</span>
              <span className="text-stone-700" data-id="element-499">
                Your personalized reset plan — based on YOUR specific symptoms,
                body type, and hormonal stage
              </span>
            </li>
          </ul>
        </div>

        <div className="w-20 h-1 bg-[#C9973A] mx-auto mb-10" data-id="element-500" />

        {/* Social Proof */}
        <div className="bg-white rounded-xl p-6 mb-8 text-center border border-stone-200" data-id="element-501">
          <div className="flex justify-center gap-1 mb-2" data-id="element-502">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 text-[#C9973A] fill-current" data-id="element-503" />)}
          </div>
          <p className="text-sm text-stone-600 mb-1" data-id="element-504">
            Taken by 14,847 women over 45
          </p>
          <p className="text-sm italic text-stone-700" data-id="element-505">
            "I finally understand WHY this was happening to me." — Karen T., Age
            51, Phoenix
          </p>
        </div>

        {/* CTA */}
        <button onClick={onStart} className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg" data-id="element-506">
          → FIND MY BELLY TYPE (FREE QUIZ)
        </button>

        {/* Disclaimer */}
        <p className="text-xs text-stone-500 text-center mt-4" data-id="element-507">
          🔒 100% Free. No credit card required. No spam — ever. Takes less than
          60 seconds. Results are instant. Your information is never shared or
          sold.
        </p>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-xs text-stone-600" data-id="element-508">
          <span data-id="element-509">🔒 Secure & Private</span>
          <span data-id="element-510">⚡ Instant Results</span>
          <span data-id="element-511">🌿 100% Natural Info</span>
          <span data-id="element-512">👩 Built for Women 45+</span>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-xs text-stone-400" data-id="element-513">
          <p data-id="element-514">Privacy Policy | Terms | Disclaimer</p>
          <p className="mt-2 max-w-md mx-auto" data-id="element-515">
            This quiz is for informational purposes only and does not constitute
            medical advice.
          </p>
        </div>
      </div>
    </div>;
}
function QuestionsSection({
  currentQuestion,
  answers,
  onAnswer,
  onMultiContinue,
  onBack
}: {
  currentQuestion: number;
  answers: Record<number, string | string[]>;
  onAnswer: (questionId: number, answer: string | string[]) => void;
  onMultiContinue: () => void;
  onBack: () => void;
}) {
  const question = QUESTIONS.find(q => q.id === currentQuestion);
  if (!question) return null;
  const currentAnswer = answers[currentQuestion];
  const isMulti = question.type === 'multi';
  const hasAnswer = isMulti ? Array.isArray(currentAnswer) && currentAnswer.length > 0 : !!currentAnswer;
  return <div className="min-h-screen flex items-center justify-center px-4 py-12" data-id="element-516">
      <div className="w-full max-w-[680px]" data-id="element-517">
        {/* Progress Bar */}
        <div className="mb-8" data-id="element-518">
          <div className="flex justify-between items-center mb-2" data-id="element-519">
            <p className="text-sm text-stone-600" data-id="element-520">
              Question {currentQuestion} of 14
            </p>
            <p className="text-sm font-semibold text-[#C9973A]" data-id="element-521">
              {question.progress}%
            </p>
          </div>
          <div className="w-full h-2 bg-stone-200 rounded-full" data-id="element-522">
            <div className="h-full bg-[#C9973A] rounded-full transition-all duration-300" style={{
            width: `${question.progress}%`
          }} data-id="element-523" />
          </div>
        </div>

        {/* Question */}
        <div className="mb-8" data-id="element-524">
          {question.headline && <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-4 text-center" data-id="element-525">
              {question.headline}
            </h2>}
          <h3 className="text-xl md:text-2xl font-bold text-[#2C2C2C] mb-3 text-center" data-id="element-526">
            {question.question}
          </h3>
          {question.subtext && <p className="text-sm italic text-stone-600 text-center max-w-xl mx-auto" data-id="element-527">
              {question.subtext}
            </p>}
        </div>

        {/* Options */}
        <div className="space-y-3 mb-8" data-id="element-528">
          {question.options.map(option => {
          const isSelected = isMulti ? Array.isArray(currentAnswer) && currentAnswer.includes(option.value) : currentAnswer === option.value;
          return <button key={option.value} onClick={() => {
            if (isMulti) {
              const current = Array.isArray(currentAnswer) ? currentAnswer : [];
              const newAnswer = isSelected ? current.filter(v => v !== option.value) : [...current, option.value];
              onAnswer(currentQuestion, newAnswer);
            } else {
              onAnswer(currentQuestion, option.value);
            }
          }} className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-[#C9973A] bg-amber-50' : 'border-stone-200 bg-white hover:border-[#C9973A] hover:bg-amber-50/50'}`} data-id="element-529">
                <div className="flex items-start gap-3" data-id="element-530">
                  {isMulti && <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${isSelected ? 'border-[#C9973A] bg-[#C9973A]' : 'border-stone-300'}`} data-id="element-531">
                      {isSelected && <Check className="w-3 h-3 text-white" data-id="element-532" />}
                    </div>}
                  <div className="flex-1" data-id="element-533">
                    <p className="font-semibold text-[#2C2C2C] mb-1" data-id="element-534">
                      {option.label}
                    </p>
                    {'description' in option && option.description && <p className="text-sm text-stone-600" data-id="element-535">
                        {option.description}
                      </p>}
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* Multi-select Continue Button */}
        {isMulti && hasAnswer && <button onClick={onMultiContinue} className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2" data-id="element-536">
            Continue <ArrowRight className="w-5 h-5" data-id="element-537" />
          </button>}

        {/* Back Button */}
        {currentQuestion > 1 && <button onClick={onBack} className="mt-6 text-sm text-stone-500 hover:text-[#C9973A] flex items-center gap-1 mx-auto transition-colors" data-id="element-538">
            <ArrowLeft className="w-4 h-4" data-id="element-539" />
            Back
          </button>}
      </div>
    </div>;
}
function LoadingSection() {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = ['Analyzing your bloating pattern and timing...', 'Cross-referencing your symptoms with hormonal profiles...', 'Identifying your dominant cortisol and inflammation markers...', 'Matching your profile to the most effective natural protocol...', 'Your personalized Menopause Belly Type has been identified. ✓'];
  useEffect(() => {
    const progressTimer = setInterval(() => {
      setProgress(p => Math.min(p + 1, 100));
    }, 100);
    const messageTimer = setInterval(() => {
      setMessageIndex(i => Math.min(i + 1, messages.length - 1));
    }, 2000);
    return () => {
      clearInterval(progressTimer);
      clearInterval(messageTimer);
    };
  }, []);
  return <div className="min-h-screen flex items-center justify-center px-4" data-id="element-540">
      <div className="w-full max-w-[480px] text-center" data-id="element-541">
        <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4" data-id="element-542">
          Analyzing Your Hormonal Belly Profile...
        </h2>
        <p className="text-lg text-stone-600 mb-2" data-id="element-543">
          Based on your 14 answers, we're identifying your exact belly type and
          the protocol that matches it.
        </p>
        <p className="text-sm text-stone-500 mb-12" data-id="element-544">
          Please don't close this page.
        </p>

        {/* Progress Bar */}
        <div className="mb-8" data-id="element-545">
          <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden" data-id="element-546">
            <div className="h-full bg-gradient-to-r from-[#C9973A] to-amber-600 transition-all duration-100" style={{
            width: `${progress}%`
          }} data-id="element-547" />
          </div>
          <div className="flex justify-between mt-2 text-xs text-stone-400" data-id="element-548">
            <span data-id="element-549">0%</span>
            <span data-id="element-550">25%</span>
            <span data-id="element-551">50%</span>
            <span data-id="element-552">75%</span>
            <span data-id="element-553">100%</span>
          </div>
        </div>

        {/* Rotating Message */}
        <p className="text-base text-stone-700 mb-12 min-h-[48px] flex items-center justify-center" data-id="element-554">
          {messages[messageIndex]}
        </p>

        {/* Bottom Text */}
        <p className="text-sm text-stone-500 italic" data-id="element-555">
          Over 14,000 women have completed this analysis. The results have
          helped women finally understand why their belly behaves the way it
          does — and what to do about it starting tomorrow morning.
        </p>
      </div>
    </div>;
}
// Results, Offer, Checkout, ThankYou sections will be in the next file due to length
// Continuing in next response...
function ResultsSection({
  resultType,
  primaryGoal,
  onContinue
}: {
  resultType: BellyType;
  primaryGoal: PrimaryGoal;
  onContinue: () => void;
}) {
  const results = {
    TYPE1: {
      badge: 'TYPE 1 — THE CORTISOL BELLY',
      badgeColor: 'border-red-400',
      subtext: "Your belly isn't a food problem. It's a stress hormone problem.",
      headlines: {
        BELLY: "Your Jeans Can Fit Again — But Not The Way You've Been Trying",
        ENERGY: 'Your Afternoon Energy Crash and Your Belly Bloat Have The Same Cause',
        SKIN: 'Your Cortisol Belly Is Also Aging Your Skin Faster Than Necessary',
        IDENTITY: "The Real You Isn't Gone — She's Just Being Blocked By Cortisol"
      },
      checklist: ['Your bloating peaks between 3–6 PM (right when cortisol hits its daily high point)', "You're exercising regularly — but it's working AGAINST your belly, not with it", 'Eating less has either not helped or made the bloating worse', 'You experience afternoon energy crashes, poor sleep, or stress-driven symptoms', 'Your belly feels flat in the morning but inflated by evening'],
      mechanism: 'After 45, your body produces significantly more cortisol — the primary stress hormone. When cortisol stays elevated, your body enters what scientists call Survival Mode. In Survival Mode, your body locks fat directly into your abdominal area to protect your organs, causes your cells to retain fluid, and makes intense exercise and low-calorie diets INCREASE the problem.',
      solution: 'The Golden Root Tonic is a 3-ingredient morning drink that sends a biological signal to your cortisol system — starting the reset process within 12 hours. The 2-Minute Cortisol Reset tells your nervous system to stop storing stress fat while you sleep.',
      cta: 'SEE MY PERSONALIZED RESET PLAN'
    },
    TYPE2: {
      badge: 'TYPE 2 — THE INFLAMMATION BELLY',
      badgeColor: 'border-orange-400',
      subtext: "Your belly isn't fat. It's your immune system misfiring.",
      headlines: {
        BELLY: 'What Looks Like Belly Fat Is Actually Inflammation — And It Responds To Nature, Not Diets',
        ENERGY: 'The Same Thing Inflaming Your Belly Is Draining Your Energy Every Day',
        SKIN: "Your Inflammation Belly Is Also Affecting Your Skin's Firmness",
        IDENTITY: "Your Body Isn't Betraying You — It's Asking For Something Specific"
      },
      checklist: ["Your belly stays bloated regardless of what you eat or don't eat", "You've tried eliminating foods, cutting calories, and supplements — with little result", 'You experience body aches, feeling heavy, or a general sense of inflammation', "The bloating doesn't follow a predictable daily pattern — it's often just always there", 'Standard diet advice has consistently failed you'],
      mechanism: 'After 45, declining estrogen levels trigger an increase in inflammatory markers throughout the body. This creates chronic, low-grade inflammation that causes your belly cells to retain fluid and swell. Many healthy foods and low-calorie diets can actually INCREASE inflammatory markers in post-menopausal women.',
      solution: "The Golden Root Tonic contains three grocery-store ingredients that research has identified as nature's most powerful inflammation modulators — including the compound nicknamed nature's Ozempic for its effect on abdominal inflammation.",
      cta: 'SEE MY INFLAMMATION RESET PLAN'
    },
    TYPE3: {
      badge: 'TYPE 3 — THE HORMONAL FLUSH BELLY',
      badgeColor: 'border-blue-400',
      subtext: "Your body is holding water it doesn't know how to release.",
      headlines: {
        BELLY: "Your Belly Isn't Bigger — It's Waterlogged. Here's How To Drain It.",
        ENERGY: 'The Trapped Fluid Is Affecting More Than Just Your Belly',
        SKIN: 'The Same Trapped Fluid Puffing Your Belly Is Puffing Your Face',
        IDENTITY: "You're Not Gaining Weight. Your Body Has Forgotten How To Let Go."
      },
      checklist: ['You experience puffiness in your face, especially in the morning', 'Your ankles or feet swell by end of day', 'Your belly fluctuates dramatically — sometimes flat, sometimes very bloated', "You're in perimenopause or early menopause (hormone fluctuations at their peak)", "Drinking more water hasn't helped"],
      mechanism: 'During perimenopause and early menopause, fluctuating estrogen and progesterone levels directly affect your lymphatic system — the network responsible for draining excess fluid from your tissues. When hormones fluctuate, the lymphatic system slows and fluid gets trapped.',
      solution: 'The 2-Minute Lymphatic Flush — a simple self-massage sequence that manually activates your lymphatic drainage, releasing trapped fluid in your face, belly, and ankles. Women report visible difference within 24–48 hours. The Golden Root Tonic reduces the hormonal inflammation causing your lymphatic system to slow down.',
      cta: 'SEE MY HORMONAL FLUSH PLAN'
    },
    TYPE4: {
      badge: 'TYPE 4 — THE METABOLIC FREEZE BELLY',
      badgeColor: 'border-green-400',
      subtext: "Your metabolism hasn't stopped working. It's been put in lockdown.",
      headlines: {
        BELLY: "Your Belly Won't Move Because Your Metabolism Is Frozen — Not Broken",
        ENERGY: 'The Fatigue and The Belly Have The Same Root Cause — And The Same Fix',
        SKIN: 'Your Frozen Metabolism Is Affecting Your Skin Too',
        IDENTITY: 'Nothing Is Wrong With You. Your Biology Just Changed The Rules.'
      },
      checklist: ['Eating less has produced zero results — or actually increased belly size', 'You feel exhausted most of the day, even after adequate sleep', 'You are post-menopausal (periods stopped 2+ years ago)', 'Your metabolism seems to have given up — nothing moves the needle anymore', "Approaches that worked in your 30s and 40s simply don't work anymore"],
      mechanism: "After menopause, the loss of estrogen causes a fundamental shift in how your body manages energy. Your metabolic rate slows and your body's threshold for entering Survival Mode drops dramatically. Calorie restriction now ACTIVELY triggers fat storage in your abdomen.",
      solution: "The Golden Root Tonic contains compounds that gently reactivate metabolic pathways that go dormant after menopause. The Cortisol Reset Protocol signals your body that it's safe to leave Survival Mode — allowing your metabolism to begin functioning at a higher rate again.",
      cta: 'SEE MY METABOLIC RESET PLAN'
    }
  };
  const result = results[resultType];
  const headline = result.headlines[primaryGoal];
  return <div className="min-h-screen py-12 px-4" data-id="element-556">
      <div className="w-full max-w-[680px] mx-auto" data-id="element-557">
        {/* Badge */}
        <div className={`border-2 ${result.badgeColor} rounded-xl p-6 mb-8 text-center bg-white`} data-id="element-558">
          <p className="text-sm font-bold text-stone-600 mb-2" data-id="element-559">
            🔴 YOUR BELLY TYPE:
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-560">
            {result.badge}
          </h2>
          <p className="text-base italic text-stone-600" data-id="element-561">{result.subtext}</p>
        </div>

        {/* Headline */}
        <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-8 text-center leading-tight" data-id="element-562">
          {headline}
        </h1>

        {/* Checklist */}
        <div className="bg-white rounded-xl p-8 mb-8 border border-stone-200" data-id="element-563">
          <h3 className="text-xl font-bold text-[#2C2C2C] mb-6" data-id="element-564">
            Here's What We Found About You
          </h3>
          <ul className="space-y-4" data-id="element-565">
            {result.checklist.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-566">
                <Check className="w-5 h-5 text-[#C9973A] flex-shrink-0 mt-0.5" data-id="element-567" />
                <span className="text-stone-700" data-id="element-568">{item}</span>
              </li>)}
          </ul>
          <p className="mt-6 font-bold text-[#2C2C2C]" data-id="element-569">
            This is a textbook {result.badge.split('—')[1].trim()} pattern.
          </p>
        </div>

        {/* Mechanism */}
        <div className="bg-amber-50 rounded-xl p-8 mb-8 border border-amber-200" data-id="element-570">
          <h3 className="text-xl font-bold text-[#2C2C2C] mb-4" data-id="element-571">
            What This Means For Your Body
          </h3>
          <p className="text-stone-700 leading-relaxed" data-id="element-572">{result.mechanism}</p>
        </div>

        {/* Solution */}
        <div className="bg-white rounded-xl p-8 mb-8 border border-stone-200" data-id="element-573">
          <h3 className="text-xl font-bold text-[#2C2C2C] mb-4" data-id="element-574">
            What YOUR Body Needs Right Now
          </h3>
          <p className="text-stone-700 leading-relaxed" data-id="element-575">{result.solution}</p>
        </div>

        {/* Transition Box */}
        <div className="bg-gradient-to-br from-[#C9973A]/10 to-amber-100/50 rounded-xl p-8 mb-8 border-2 border-[#C9973A]" data-id="element-576">
          <p className="text-stone-800 leading-relaxed mb-6" data-id="element-577">
            Based on your results, we've prepared your complete 7-Day{' '}
            {result.badge.split('—')[1].trim()} Reset Plan. It takes 5 minutes
            each morning. Uses 3 ingredients from your grocery store. And it's
            designed specifically for how YOUR body works after 45.
          </p>
          <p className="font-bold text-[#2C2C2C]" data-id="element-578">
            Your plan is waiting on the next page.
          </p>
        </div>

        {/* CTA */}
        <button onClick={onContinue} className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg mb-8" data-id="element-579">
          {result.cta} <ArrowRight className="w-5 h-5" data-id="element-580" />
        </button>

        {/* Testimonial */}
        <div className="text-center mb-8" data-id="element-581">
          <div className="flex justify-center gap-1 mb-2" data-id="element-582">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-[#C9973A] fill-current" data-id="element-583" />)}
          </div>
          <p className="text-sm italic text-stone-600" data-id="element-584">
            "I finally understood WHY my belly was doing this. Then I fixed it."
            — Margaret T., Age 54, Atlanta
          </p>
        </div>

        {/* Trust Icons */}
        <div className="flex flex-wrap justify-center gap-4 text-xs text-stone-600 mb-8" data-id="element-585">
          <span data-id="element-586">🔒 Instant Digital Download</span>
          <span data-id="element-587">⚡ Start Tomorrow Morning</span>
          <span data-id="element-588">💰 Less Than a Dinner Out</span>
          <span data-id="element-589">🛡️ 7-Day Money-Back Guarantee</span>
        </div>

        {/* Secondary CTA */}
        <button onClick={onContinue} className="w-full bg-stone-800 hover:bg-stone-900 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 flex items-center justify-center gap-2" data-id="element-590">
          → SEE MY PERSONALIZED PLAN NOW
        </button>

        <p className="text-xs italic text-stone-500 text-center mt-4" data-id="element-591">
          Your results and personalized plan were prepared based on your 14
          answers. They will not be saved after you leave this page.
        </p>
      </div>
    </div>;
}
function OfferSection({
  onContinue
}: {
  onContinue: () => void;
}) {
  const checkoutUrl = 'https://www.voicesoffreedomus.com/cart/45604499587258:1';
  return <div className="min-h-screen py-12 px-4" data-id="element-592">
      <div className="w-full max-w-[680px] mx-auto" data-id="element-593">
        {/* Personalized Banner */}
        <div className="bg-[#C9973A] text-white text-center py-3 px-4 rounded-xl mb-8" data-id="element-594">
          <p className="font-semibold" data-id="element-595">Based on Your Results:</p>
        </div>

        {/* Main Headline */}
        <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4 text-center leading-tight" data-id="element-596">
          Finally — A 5-Minute Ancient Morning Ritual That Flushes Menopause
          Bloat, Resets Your Cortisol, and Firms Your Skin Starting Tomorrow
          Morning
        </h1>
        <p className="text-lg text-stone-600 mb-8 text-center" data-id="element-597">
          The Golden Root Ritual is a 7-Day Transformation Blueprint designed
          specifically for women 45+ — using 3 grocery store ingredients that
          work WITH your hormones, not against them.
        </p>

        {/* Product Image */}
        <div className="mb-8" data-id="element-598">
          <img src="https://cdn.magicpatterns.com/uploads/sqWj2RGF8QLq23vTiDj7BE/heylloyd-image-1771222482108.png" alt="The Golden Root Ritual" className="w-full max-w-md mx-auto rounded-xl shadow-xl" data-id="element-599" />
        </div>

        {/* CTA */}
        <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg mb-4" data-id="element-600">
          → YES! GET MY RESET PLAN FOR $17
        </a>

        <div className="text-center mb-12" data-id="element-601">
          <p className="text-stone-400 line-through text-lg" data-id="element-602">$97</p>
          <p className="text-3xl font-bold text-[#C9973A]" data-id="element-603">
            Today Only: $17 USD
          </p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-604">
            Instant Digital Download — PDF Format
          </p>
        </div>

        {/* Problem Section */}
        <div className="bg-stone-50 rounded-xl p-8 mb-8" data-id="element-605">
          <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4" data-id="element-606">
            Do You Feel Betrayed By Your Own Body?
          </h2>
          <p className="text-stone-700 leading-relaxed mb-4" data-id="element-607">
            You wake up feeling light, but by 4:00 PM, your jeans feel two sizes
            too small. You're eating salads, walking 10,000 steps, and cutting
            out sugar… yet the menopause belly won't budge.
          </p>
          <p className="text-stone-700 leading-relaxed" data-id="element-608">
            After 45, your Cortisol (stress hormone) is hijacking your
            metabolism. When you stress your body with intense exercise or low
            calories, it goes into Survival Mode — locking fat directly in your
            abdomen to protect your organs.
          </p>
        </div>

        {/* What's Inside */}
        <div className="mb-8" data-id="element-609">
          <h2 className="text-2xl font-bold text-[#2C2C2C] mb-6 text-center" data-id="element-610">
            What's Inside The Golden Root Ritual
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-6" data-id="element-611">
            <div className="bg-white rounded-xl p-6 border border-[#C9973A]" data-id="element-612">
              <h3 className="font-bold text-[#2C2C2C] mb-2" data-id="element-613">
                🍵 The Golden Root Tonic
              </h3>
              <p className="text-sm text-stone-600" data-id="element-614">
                The exact 3-ingredient recipe that shuts down systemic
                inflammation in 12 hours.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#C9973A]" data-id="element-615">
              <h3 className="font-bold text-[#2C2C2C] mb-2" data-id="element-616">
                👐 The 2-Minute Lymphatic Flush
              </h3>
              <p className="text-sm text-stone-600" data-id="element-617">
                A simple self-massage technique to drain trapped fluid.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#C9973A]" data-id="element-618">
              <h3 className="font-bold text-[#2C2C2C] mb-2" data-id="element-619">
                🧠 The Cortisol Reset
              </h3>
              <p className="text-sm text-stone-600" data-id="element-620">
                How to signal your brain to stop storing stress fat while you
                sleep.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#C9973A]" data-id="element-621">
              <h3 className="font-bold text-[#2C2C2C] mb-2" data-id="element-622">
                🛒 The 7-Day Grocery List
              </h3>
              <p className="text-sm text-stone-600" data-id="element-623">
                Everything you need to reset your biology without breaking the
                bank.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-8" data-id="element-624">
          <h2 className="text-2xl font-bold text-[#2C2C2C] mb-6 text-center" data-id="element-625">
            Real Women. Real Results.
          </h2>
          <div className="space-y-4" data-id="element-626">
            {[{
            name: 'Sarah M.',
            age: '52',
            city: 'Miami',
            quote: 'I lost 2 inches off my waist in 5 days—without cutting calories. The bloating that plagued me for 3 years is GONE.'
          }, {
            name: 'Jennifer K.',
            age: '48',
            city: 'Austin',
            quote: "By day 3, my jeans fit again. By day 7, my husband noticed my face looked less puffy. This is the first thing that's actually worked since menopause hit."
          }, {
            name: 'Patricia L.',
            age: '55',
            city: 'Denver',
            quote: 'I spent $200/month on supplements that did nothing. This $17 guide gave me more results in one week than 2 years of expensive pills.'
          }].map((t, i) => <div key={i} className="bg-white rounded-xl p-6 border border-stone-200" data-id="element-627">
                <div className="flex gap-1 mb-3" data-id="element-628">
                  {[1, 2, 3, 4, 5].map(j => <Star key={j} className="w-4 h-4 text-[#C9973A] fill-current" data-id="element-629" />)}
                </div>
                <p className="text-stone-700 italic mb-4" data-id="element-630">"{t.quote}"</p>
                <p className="text-sm font-bold text-[#2C2C2C]" data-id="element-631">{t.name}</p>
                <p className="text-xs text-stone-500" data-id="element-632">
                  Age {t.age}, {t.city}
                </p>
              </div>)}
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white rounded-xl p-8 mb-8 border-2 border-[#C9973A] text-center" data-id="element-633">
          <h2 className="text-2xl font-bold text-[#2C2C2C] mb-6" data-id="element-634">
            Get The Complete Golden Root Ritual Today
          </h2>
          <div className="mb-6" data-id="element-635">
            <p className="text-stone-600 mb-2" data-id="element-636">
              A high-end wellness clinic consultation:{' '}
              <span className="line-through" data-id="element-637">$350</span>
            </p>
            <p className="text-stone-600 mb-2" data-id="element-638">
              High-end skincare creams (monthly):{' '}
              <span className="line-through" data-id="element-639">$150</span>
            </p>
            <p className="text-stone-600 mb-6" data-id="element-640">
              Premium supplements (monthly):{' '}
              <span className="line-through" data-id="element-641">$200</span>
            </p>
            <div className="mb-6" data-id="element-642">
              <p className="text-4xl text-stone-400 line-through mb-2" data-id="element-643">$97</p>
              <p className="text-6xl font-bold text-[#C9973A]" data-id="element-644">$17</p>
              <p className="text-sm text-stone-500 mt-2" data-id="element-645">
                One-Time Payment — Never Recurring
              </p>
              <p className="text-xs text-stone-400 mt-1" data-id="element-646">
                Less than a dinner out
              </p>
            </div>
          </div>

          <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg mb-4" data-id="element-647">
            → SEND ME THE GOLDEN ROOT RITUAL NOW
          </a>

          <div className="flex flex-wrap justify-center gap-4 text-xs text-stone-600" data-id="element-648">
            <span className="flex items-center gap-1" data-id="element-649">
              <Lock className="w-3 h-3" data-id="element-650" /> SSL Secure
            </span>
            <span className="flex items-center gap-1" data-id="element-651">
              <Download className="w-3 h-3" data-id="element-652" /> Instant Download
            </span>
            <span className="flex items-center gap-1" data-id="element-653">
              <ShieldCheck className="w-3 h-3" data-id="element-654" /> 7-Day Guarantee
            </span>
          </div>
        </div>

        {/* Guarantee */}
        <div className="bg-green-50 rounded-xl p-8 mb-8 border-2 border-green-200" data-id="element-655">
          <h3 className="text-xl font-bold text-[#2C2C2C] mb-4 flex items-center gap-2" data-id="element-656">
            <ShieldCheck className="w-6 h-6 text-green-600" data-id="element-657" />
            The 7-Day "Better Jeans" Guarantee
          </h3>
          <p className="text-stone-700 leading-relaxed" data-id="element-658">
            I am so confident this ritual works that I'm taking all the risk.
            Download the guide, try the tonic, and do the 5-minute ritual for 7
            days. If your jeans don't feel looser and your energy isn't higher,
            just send me an email and I'll refund every penny. No questions
            asked.
          </p>
        </div>

        {/* Final CTA */}
        <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg" data-id="element-659">
          → I CHOOSE CHANGE — SEND ME THE RITUAL
        </a>

        <p className="text-center text-sm text-stone-600 mt-6 italic" data-id="element-660">
          Stop Fighting Your Body. Start Working With It.
        </p>
      </div>
    </div>;
}
function CheckoutSection({
  onSubmit
}: {
  onSubmit: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    card: '',
    expiry: '',
    cvv: '',
    zip: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return <div className="min-h-screen py-12 px-4" data-id="element-661">
      <div className="w-full max-w-[560px] mx-auto" data-id="element-662">
        <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2 text-center" data-id="element-663">
          You're One Step Away From Your Personalized Reset Plan
        </h1>
        <p className="text-stone-600 mb-8 text-center" data-id="element-664">
          Complete your secure order below. You'll receive instant access via
          email.
        </p>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-6 mb-8 border-2 border-[#C9973A]" data-id="element-665">
          <h2 className="font-bold text-[#2C2C2C] mb-4" data-id="element-666">Order Summary</h2>
          <div className="space-y-2 text-sm" data-id="element-667">
            <div className="flex justify-between" data-id="element-668">
              <span className="text-stone-600" data-id="element-669">Product:</span>
              <span className="font-semibold" data-id="element-670">
                The Golden Root Ritual — 7-Day Blueprint
              </span>
            </div>
            <div className="flex justify-between" data-id="element-671">
              <span className="text-stone-600" data-id="element-672">Format:</span>
              <span data-id="element-673">Instant Digital Download (PDF)</span>
            </div>
            <div className="flex justify-between" data-id="element-674">
              <span className="text-stone-600" data-id="element-675">Regular Price:</span>
              <span className="line-through text-stone-400" data-id="element-676">$97</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t" data-id="element-677">
              <span data-id="element-678">Your Price Today:</span>
              <span className="text-[#C9973A]" data-id="element-679">$17.00 USD</span>
            </div>
            <p className="text-xs text-green-600 pt-2" data-id="element-680">Savings: $80.00</p>
          </div>
        </div>

        {/* Payment Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-681">
          <div data-id="element-682">
            <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-683">
              Full Name
            </label>
            <input type="text" required value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="Jane Smith" data-id="element-684" />
          </div>

          <div data-id="element-685">
            <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-686">
              Email Address
            </label>
            <input type="email" required value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="jane@example.com" data-id="element-687" />
          </div>

          <div data-id="element-688">
            <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-689">
              Card Number
            </label>
            <input type="text" required value={formData.card} onChange={e => setFormData({
            ...formData,
            card: e.target.value
          })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="4242 4242 4242 4242" data-id="element-690" />
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-691">
            <div data-id="element-692">
              <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-693">
                Expiry
              </label>
              <input type="text" required value={formData.expiry} onChange={e => setFormData({
              ...formData,
              expiry: e.target.value
            })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="MM/YY" data-id="element-694" />
            </div>
            <div data-id="element-695">
              <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-696">
                CVV
              </label>
              <input type="text" required value={formData.cvv} onChange={e => setFormData({
              ...formData,
              cvv: e.target.value
            })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="123" data-id="element-697" />
            </div>
            <div data-id="element-698">
              <label className="block text-sm font-semibold text-[#2C2C2C] mb-2" data-id="element-699">
                Zip
              </label>
              <input type="text" required value={formData.zip} onChange={e => setFormData({
              ...formData,
              zip: e.target.value
            })} className="w-full px-4 py-3 rounded-lg border border-stone-300 focus:border-[#C9973A] focus:ring-2 focus:ring-[#C9973A]/20 outline-none transition-all" placeholder="12345" data-id="element-700" />
            </div>
          </div>

          <button type="submit" className="w-full bg-gradient-to-r from-[#C9973A] to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-lg" data-id="element-701">
            → COMPLETE MY ORDER — GET INSTANT ACCESS
          </button>

          <p className="text-xs text-center text-stone-500 flex items-center justify-center gap-2" data-id="element-702">
            <Lock className="w-3 h-3" data-id="element-703" />
            256-bit SSL Encryption | Your payment info is never stored
          </p>
        </form>

        {/* Guarantee Reminder */}
        <div className="bg-green-50 rounded-xl p-6 mt-8 border border-green-200" data-id="element-704">
          <p className="text-sm text-stone-700 flex items-start gap-2" data-id="element-705">
            <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-706" />
            <span data-id="element-707">
              <strong data-id="element-708">Remember:</strong> You're fully protected by the 7-Day
              Better Jeans Guarantee. If you don't feel a difference in 7 days,
              email us for a full refund. Zero risk.
            </span>
          </p>
        </div>

        {/* Trust Elements */}
        <div className="flex flex-wrap justify-center gap-4 mt-8 text-xs text-stone-600" data-id="element-709">
          <span data-id="element-710">✅ Instant Access</span>
          <span data-id="element-711">🔒 Secure Checkout</span>
          <span data-id="element-712">🛡️ 7-Day Guarantee</span>
          <span data-id="element-713">📧 24hr Email Support</span>
        </div>

        <div className="text-center mt-8" data-id="element-714">
          <div className="flex justify-center gap-1 mb-2" data-id="element-715">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 text-[#C9973A] fill-current" data-id="element-716" />)}
          </div>
          <p className="text-xs italic text-stone-600" data-id="element-717">
            "Took 2 minutes to order. Downloaded immediately. Started that
            night." — Donna R., Age 50, Nashville
          </p>
        </div>
      </div>
    </div>;
}
function ThankYouSection() {
  return <div className="min-h-screen flex items-center justify-center px-4 py-12" data-id="element-718">
      <div className="w-full max-w-[680px] text-center" data-id="element-719">
        <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4" data-id="element-720">
          🎉 Your Order Is Confirmed!
        </h1>
        <p className="text-xl text-stone-600 mb-8" data-id="element-721">
          Check your email — your download link is on its way. (Check spam if
          you don't see it within 2 minutes.)
        </p>

        <div className="bg-white rounded-xl p-8 mb-8 border border-stone-200 text-left" data-id="element-722">
          <p className="text-stone-700 mb-6" data-id="element-723">
            Thank you for investing in yourself today. You now have everything
            you need to start your 7-Day Golden Root Ritual tomorrow morning.
          </p>

          <h2 className="font-bold text-[#2C2C2C] mb-4" data-id="element-724">
            Here's what to do right now:
          </h2>
          <ol className="space-y-3 text-stone-700" data-id="element-725">
            <li className="flex items-start gap-3" data-id="element-726">
              <span className="font-bold text-[#C9973A] flex-shrink-0" data-id="element-727">1.</span>
              <span data-id="element-728">Check your email for your download link</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-729">
              <span className="font-bold text-[#C9973A] flex-shrink-0" data-id="element-730">2.</span>
              <span data-id="element-731">Download the PDF to your phone or tablet</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-732">
              <span className="font-bold text-[#C9973A] flex-shrink-0" data-id="element-733">3.</span>
              <span data-id="element-734">Read pages 1–12 tonight (takes 15 minutes)</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-735">
              <span className="font-bold text-[#C9973A] flex-shrink-0" data-id="element-736">4.</span>
              <span data-id="element-737">Prepare your first tonic in the morning</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-738">
              <span className="font-bold text-[#C9973A] flex-shrink-0" data-id="element-739">5.</span>
              <span data-id="element-740">Come back and tell us how Day 7 went</span>
            </li>
          </ol>
        </div>

        <div className="bg-amber-50 rounded-xl p-8 border border-amber-200" data-id="element-741">
          <p className="text-lg text-stone-800 leading-relaxed mb-4" data-id="element-742">
            You've spent years trying to fight your body. Starting tomorrow, you
            work with it.
          </p>
          <p className="text-lg text-stone-800 leading-relaxed" data-id="element-743">
            The woman you were before menopause changed everything? She's still
            in there. She's just been waiting for the right signal.
          </p>
          <p className="text-xl font-bold text-[#C9973A] mt-4" data-id="element-744">
            Now you have it.
          </p>
        </div>

        <div className="mt-12 text-xs text-stone-400" data-id="element-745">
          <p data-id="element-746">Privacy Policy | Terms | Disclaimer</p>
          <p className="mt-2" data-id="element-747">
            © 2026 The Golden Root Ritual. All rights reserved.
          </p>
        </div>
      </div>
    </div>;
}